import { Box, Divider, Text } from '@chakra-ui/react';
import { singleDigitDateFormat } from '@web-app/utils/date-time.utils';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { FC, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import { ChartingNoteProps } from './types';

const ChartingNote: FC<ChartingNoteProps> = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { chartingNoteTitle, chartingNoteData, body, onDidExpand } = props;

  const { appointmentTypeName, formattedDate } = useMemo(() => {
    const appointment = chartingNoteData?.form_answer_group?.appointment;
    if (appointment?.appointment_type?.name && appointment?.date) {
      try {
        const date = new Date(appointment.date);
        const formatted = format(date, singleDigitDateFormat);
        return {
          appointmentTypeName: appointment.appointment_type.name,
          formattedDate: formatted,
        };
      } catch {
        // Fallback to appointment type name if date parsing fails
        return {
          appointmentTypeName: appointment.appointment_type.name,
          formattedDate: null,
        };
      }
    }
    // Fallback to original title if no appointment data
    return {
      appointmentTypeName: chartingNoteTitle,
      formattedDate: null,
    };
  }, [chartingNoteTitle, chartingNoteData]);

  const handleNoteClick = () => {
    if (document.getSelection?.()?.type === 'Range') return;

    setIsExpanded(!isExpanded);
  };

  // Call onDidExpand after content is rendered when expanded
  useEffect(() => {
    if (isExpanded && onDidExpand) {
      // Use requestAnimationFrame to ensure DOM has rendered
      onDidExpand();
    }
  }, [isExpanded, onDidExpand]);

  return (
    <Box
      position="relative"
      className="charting-note amp-mask excluded-phi"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        onClick={handleNoteClick}
      >
        <Text fontWeight="600">{appointmentTypeName}</Text>
        <Box display="flex" alignItems="center" gap={2}>
          {formattedDate && (
            <Text fontWeight="600" color="text.muted">
              {formattedDate}
            </Text>
          )}
          {isExpanded ? <FiChevronUp size="20" /> : <FiChevronDown size="20" />}
        </Box>
      </Box>
      {isExpanded && (
        <Box marginTop="10px">
          <Divider my="8px" borderColor="border.subtle" />
          {body ??
            chartingNoteData?.form_answer_group?.form_answers?.map((answer) => (
              <Box key={answer?.label} marginY="8px" pl="0.5rem">
                <Text fontSize="14px" fontWeight="600">
                  {answer?.label}:
                </Text>
                <Text
                  fontSize="14px"
                  ml="0.5rem"
                  borderLeftWidth="1px"
                  borderLeftColor="border.subtle"
                  pl="0.5rem"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(answer?.answer),
                    }}
                  />
                </Text>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default ChartingNote;
